import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import moment from 'moment';
import React from 'react';

import { getCustomInvoiceDownloadUrls } from '@/Billing/api';
import { formatDueDate, getDueDate } from '@/Billing/dueDateUtils';
import NoRows from '@/components/tableNoRows';

import { IUnitBill } from '../View';

export const BillHistoryTable = ({
  data,
  timeZone,
}: {
  data: IUnitBill[];
  timeZone: string;
}): JSX.Element => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const [tz] = React.useState(timeZone === '' ? moment.tz.guess() : timeZone);

  // Per-invoice amounts for SimpleSUB (non-Stripe) invoices, keyed by billing
  // invoice id. `stripeTotal` only exists for Stripe invoices; the authoritative
  // custom-invoice amount is `amount_due_cents` from the download-urls endpoint,
  // the same number printed on the PDF. Fetched lazily per bill, and only for
  // invoices that actually need it (no Stripe total AND a rendered PDF, since
  // the cents are null before the PDF exists). One failing bill must not blank
  // the whole table; its rows just keep the '-' fallback.
  const [customAmountCents, setCustomAmountCents] = React.useState<Map<string, number>>(new Map());
  React.useEffect(() => {
    const billsNeedingAmounts = data
      .map((bill) => ({
        billId: bill.id,
        invoiceIds: bill.invoices
          .filter((inv) => inv.stripeTotal === null && inv.customInvoicePdfUrl !== null)
          .map((inv) => inv.id),
      }))
      .filter((b) => b.invoiceIds.length > 0);
    if (billsNeedingAmounts.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      const entries: [string, number][] = [];
      await Promise.all(
        billsNeedingAmounts.map(async ({ billId, invoiceIds }) => {
          try {
            const resp = await getCustomInvoiceDownloadUrls(billId, invoiceIds);
            resp.custom_invoices.forEach((ci) => {
              if (ci.amount_due_cents !== null) {
                entries.push([ci.billing_invoice_id, ci.amount_due_cents]);
              }
            });
          } catch {
            // getCustomInvoiceDownloadUrls already toasts and logs.
          }
        }),
      );
      if (!cancelled && entries.length) setCustomAmountCents(new Map(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [data]);

  const renderStatusColor = (status: string): string => {
    switch (status) {
      case 'not_created':
        return '#E0E0E0';
      case 'draft':
      case 'generated':
        return '#D0E9FF';
      case 'open':
        return '#FFEFD0';
      case 'paid':
        return '#DFF7DF';
      case 'uncollectable':
        return '#FFD6D6';
      case 'void':
        return '#E4D6FF';
      default:
        return '#E0E0E0';
    }
  };

  // Stripe invoices carry a real Stripe lifecycle status. SimpleSUB (non-Stripe)
  // invoices also store 'not_created' in stripeLastStatus, which only means "no
  // Stripe invoice exists" and reads as if the invoice itself was never made.
  // For those, derive a truthful status from whether the PDF has been rendered.
  const displayStatus = (inv: IUnitBill['invoices'][number]): string => {
    if (inv.stripeInvoiceId !== null) return inv.stripeLastStatus;
    return inv.customInvoicePdfUrl !== null ? 'generated' : 'not_generated';
  };

  return (
    <div>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ position: 'relative' }}>
                Amount
              </TableCell>
              <TableCell align="center" sx={{ position: 'relative' }}>
                Due
              </TableCell>
              {/* <TableCell align="center" sx={{ position: 'relative' }}>
                Email
              </TableCell> */}
              <TableCell align="center" sx={{ position: 'relative' }}>
                Billing Period
              </TableCell>
              <TableCell align="center" sx={{ position: 'relative' }}>
                Status
              </TableCell>
              <TableCell align="center" sx={{ position: 'relative' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length !== 0 ? (
              data.map((e, _i) => {
                return e.invoices.map((d) => {
                  return (
                    <TableRow>
                      <TableCell align="center">
                        {d.stripeTotal !== null
                          ? `$${d.stripeTotal.toFixed(2)}`
                          : customAmountCents.has(d.id)
                          ? `$${(customAmountCents.get(d.id)! / 100).toFixed(2)}`
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          // Stored custom-invoice due date wins; the 21-day
                          // calculation is only a fallback for Stripe invoices,
                          // which carry no stored due date on this shape
                          const dueDate = getDueDate(
                            d.customInvoiceDueDate,
                            e.billsSentTimestamp ? e.billsSentTimestamp : e.endDate,
                            21,
                            timeZone,
                          );
                          return dueDate !== null ? formatDueDate(dueDate, timeZone) : '-';
                        })()}
                      </TableCell>
                      {/* <TableCell align="center">some@email.com</TableCell> */}
                      <TableCell align="center">
                        {dayjs.unix(e.startDate).tz(tz).format('MMM D')} -{' '}
                        {dayjs.unix(e.endDate).tz(tz).format('MMM D')}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: renderStatusColor(displayStatus(d)),
                            color: displayStatus(d) === 'paid' ? '#198754' : '#5A5A5A',
                            borderRadius: '16px',
                            fontWeight: 'bold',
                            padding: '4px 12px',
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            boxShadow: 1,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {displayStatus(d).replace(/_/g, ' ').toUpperCase()}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          disabled={
                            d.customInvoicePdfUrl === null && d.stripeInvoicePdfUrl === null
                          }
                          onClick={() =>
                            (window.location.href = (d.customInvoicePdfUrl ??
                              d.stripeInvoicePdfUrl)!)
                          }
                        >
                          <SimCardDownloadOutlinedIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                });
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} sx={{ marginTop: 2 }}>
                  <NoRows title="No Rows" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
